import React from 'react'
import { useT } from '@transifex/react'

import { Spinner } from 'src/components/ui/spinner'
import { useDesignMode } from 'src/design-mode/useDesignMode'
import { cn } from 'src/lib/utils'

import { useLoadingDelay } from './useLoadingDelay'

/* -------------------------------------------------------------------------
 * A navigation-level loading WALL, dispatched on the design toggle.
 *
 * THE DISTINCTION THIS FILE EXISTS TO KEEP. A spinner says "something is
 * happening"; a skeleton says "content of THIS shape is coming". A skeleton
 * standing in for one small piece of content, in its real shape, in its real
 * place, is doing the job a spinner cannot — and stays. What the captain
 * reported is the other case: a route or a whole section that answers a
 * navigation with a BLOCK of grey rectangles which resemble nothing in
 * particular, on every navigation, so the app reads as slower than it is.
 * Those walls are what this component replaces, and only those.
 *
 * `children` is the existing skeleton wall, rendered UNCHANGED on the antd
 * rendering — the shipped default, so it is every page load of anyone who has
 * not picked "New design" under Theme (see `src/design-mode/store`). That
 * pass-through is the safety property of this track: the old wall is still
 * exactly the old wall, one flip away, while shadcn arrives one surface at a
 * time. With the toggle on, the wall becomes one quiet centred spinner, and it
 * does not appear at
 * all until `LOADING_DELAY_MS` has passed (see `./constants`).
 *
 * The Skeleton COMPONENT itself is another lane's port. Nothing here touches
 * it: this changes only where a loading state is COMPOSED.
 * ------------------------------------------------------------------------- */
export type LoadingWallProps = {
  /** The existing skeleton wall. Rendered as-is while the toggle is off. */
  children: React.ReactNode
  /**
   * How much vertical room the spinner should claim, so a page that was
   * holding its height with skeleton blocks does not collapse. A route-level
   * wall wants a viewport-ish height; a section-level one wants the section's.
   */
  minHeight?: number | string
  /**
   * Accessible name for the spinner. Defaults to a translated "Loading" —
   * this component always renders inside a resolved locale. The one surface
   * that does not (`RouteChunkSkeleton`, the boot gate) passes the bare
   * literal itself. The `Loading` source string is not registered with
   * Transifex, so a non-EN tenant falls back to the English source until it
   * is pushed; see the header of `src/components/ui/spinner.tsx`.
   */
  label?: string
  className?: string
}

const SpinnerWall = ({
  minHeight,
  label,
  className,
}: Omit<LoadingWallProps, 'children'>) => {
  const t = useT()
  const elapsed = useLoadingDelay()

  return (
    <div
      aria-busy="true"
      data-test="loading-wall"
      // The colour sits HERE and not on the Spinner: `.anticon { color:
      // inherit }` is unlayered and would outrank a colour utility on the
      // glyph itself. See the header of `src/components/ui/spinner.tsx`.
      //
      // `text-muted-foreground`, not `text-primary`: the captain's call is
      // that a loading state is not the page's subject, so it takes the
      // design system's secondary/muted foreground rather than the brand
      // blue. Every loading surface behind the toggle uses this same token —
      // `src/antd/Spin/Spin.shadcn.tsx` and
      // `src/antd/Spin/AntdDefaultIndicator.tsx` — so a spinner is the same
      // grey wherever it appears.
      className={cn(
        'flex w-full items-center justify-center text-muted-foreground',
        className
      )}
      style={{ minHeight }}
    >
      {/* Nothing at all until the threshold passes: a fast navigation paints
          content and never shows a loading state. The wrapper still renders,
          so the layout does not jump when the spinner arrives. */}
      {elapsed ? (
        <Spinner className="text-3xl" label={label ?? t('Loading')} />
      ) : null}
    </div>
  )
}

export const LoadingWall = ({ children, ...rest }: LoadingWallProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <SpinnerWall {...rest} />

  return <>{children}</>
}
